import React, { useEffect, useState } from 'react';
import { fetchStatsData } from './db/dbStats.js';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import moment from 'moment';
import './styles/Stats.css';

const STATS_CACHE_KEY  = 'statsData';
const TOTALS_CACHE_KEY = 'statsTotals';

const TIME_PERIODS = [
    { id: '7d',  label: '7D' },
    { id: '30d', label: '30D' },
    { id: 'all', label: 'All' },
];

const STATS_CONFIG = [
    { key: 'totalMinted',  label: 'Total Mythics Minted' },
    { key: 'uniqueMinted', label: 'Unique Mythics Minted' },
    { key: 'totalOwners',  label: 'Total Mythic Owners' },
];

function getCachedStats() {
    const raw = localStorage.getItem(STATS_CACHE_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
}

function setCachedStats(data) {
    localStorage.setItem(STATS_CACHE_KEY, JSON.stringify(data));
}

function getCachedTotals() {
    const raw = localStorage.getItem(TOTALS_CACHE_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
}

function sliceByPeriod(arr, period) {
    if (period === 'all' || arr.length === 0) return arr;
    const days = period === '7d' ? 7 : 30;
    const cutoff = moment().subtract(days, 'days').startOf('day');
    const filtered = arr.filter(e => moment(e.date).isSameOrAfter(cutoff));
    return filtered.length >= 2 ? filtered : arr.slice(-2);
}

const DEFAULT_PERIODS = { totalMinted: 'all', uniqueMinted: 'all', totalOwners: 'all' };

const Stats = () => {
    const [finalTotals, setFinalTotals] = useState(() => getCachedTotals());
    const [statsArray, setStatsArray] = useState([]);
    const [chartsLoading, setChartsLoading] = useState(true);
    // Each card has its own independent time period
    const [periods, setPeriods] = useState(DEFAULT_PERIODS);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadCharts = async () => {
            setChartsLoading(true);
            try {
                let data = getCachedStats();
                if (!data) {
                    data = await fetchStatsData();
                    if (data && data.length > 0) setCachedStats(data);
                }
                if (data && data.length > 0) {
                    const sorted = [...data]
                        .filter(e => e && e.totalMinted != null)
                        .sort((a, b) => new Date(a.date) - new Date(b.date));

                    if (sorted.length > 0) {
                        const last = sorted[sorted.length - 1];
                        setFinalTotals(prev => prev ?? {
                            totalMinted:  last.totalMinted  ?? 0,
                            uniqueMinted: last.uniqueMinted ?? 0,
                            totalOwners:  last.totalOwners  ?? 0,
                        });
                        setStatsArray(sorted);
                    } else {
                        setError('No stats data available yet. Run the cron job from the admin panel.');
                    }
                } else {
                    if (!getCachedTotals()) {
                        setError('No stats data available yet. Run the cron job from the admin panel.');
                    }
                }
            } catch (err) {
                if (err?.code === 'permission-denied') {
                    setError('Database permissions not configured yet. Update Firestore security rules and run the cron job from the admin panel.');
                } else if (!getCachedTotals()) {
                    setError('Failed to load stats. Please try again.');
                }
            } finally {
                setChartsLoading(false);
            }
        };

        loadCharts();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const buildChartData = (key, period) => {
        const visible = sliceByPeriod(statsArray, period);
        return {
            labels: visible.map(e => moment(e.date).format('DD MMM YYYY')),
            datasets: [{
                data: visible.map(e => e[key]),
                borderColor: '#ffd50d',
                backgroundColor: 'rgba(255, 213, 13, 0.1)',
                fill: true,
                tension: 0.35,
                pointBackgroundColor: '#ffd50d',
                pointRadius: visible.length <= 14 ? 3 : 0,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
            }],
        };
    };

    const chartOptions = {
        animation: { duration: 350, easing: 'easeInOutQuart' },
        scales: {
            x: { display: false },
            y: { display: false },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items) => items[0]?.label || '',
                    label: (item) => ` ${item.formattedValue}`,
                },
                backgroundColor: '#011a36',
                titleColor: 'rgba(255,255,255,0.5)',
                bodyColor: '#ffd50d',
                padding: 10,
                cornerRadius: 6,
            },
        },
        interaction: { intersect: false, mode: 'nearest' },
        maintainAspectRatio: false,
    };

    if (error && !finalTotals) {
        return (
            <div className="error-message">
                <div className="error-message-card">
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="stats-container" role="main">
            {STATS_CONFIG.map(({ key, label }) => (
                <div key={key} className="stat-card">
                    <p className="stat-card-label">{label}</p>

                    {finalTotals
                        ? <p className="stat-card-value">{(finalTotals[key] ?? 0).toLocaleString()}</p>
                        : <span className="skeleton" style={{ height: '36px', width: '38%', borderRadius: '6px', margin: '6px 0 10px', display: 'block' }} />
                    }

                    <div className="stat-card-chart">
                        {chartsLoading || statsArray.length === 0
                            ? <span className="skeleton" style={{ display: 'block', width: '100%', height: '100%', borderRadius: '6px' }} />
                            : <Line data={buildChartData(key, periods[key])} options={chartOptions} />
                        }
                    </div>

                    {/* Per-card period selector — only shown once chart data is ready */}
                    {!chartsLoading && statsArray.length > 0 && (
                        <div className="stat-period-row">
                            {TIME_PERIODS.map(({ id, label: pl }) => (
                                <button
                                    key={id}
                                    className={`stat-period-btn${periods[key] === id ? ' active' : ''}`}
                                    onClick={() => setPeriods(prev => ({ ...prev, [key]: id }))}
                                >
                                    {pl}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default Stats;
